/**
 * AdminClientProjectsPage — every client project, for the admin surface.
 *
 * Reads GET /api/admin/projects (AdminDashboardService.getProjects). Search,
 * status filter and pagination live in the URL, matching the other admin lists.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Skeleton,
  Alert,
  Button,
  Link,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Stack,
  LinearProgress,
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';

import adminService from '../services/adminService';
import { panelSx, tableSx, tableRowSx } from '../components/admin/surfaces';
import { brand, projectStatus, status as statusTokens, text, bg, border } from '../theme/colors';
import { formatCurrency } from '../utils/helpers';

const COLUMNS = ['Project', 'Client', 'Contractor', 'Status', 'Budget', 'Progress', 'Created'];

const PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 400;

// projects_status_check — the only five the column permits.
const STATUS_OPTIONS = ['planning', 'open_for_bids', 'in_progress', 'completed', 'cancelled'];

const EMPTY = '—';

const titleise = (value) => (value || '').replace(/_/g, ' ');

const formatDate = (value) => {
  if (!value) return EMPTY;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const AdminClientProjectsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const parsedPage = parseInt(searchParams.get('page'), 10);
  const page = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage - 1 : 0;

  const parsedLimit = parseInt(searchParams.get('limit'), 10);
  const rowsPerPage = PAGE_SIZES.includes(parsedLimit) ? parsedLimit : DEFAULT_PAGE_SIZE;

  const urlSearch = searchParams.get('search') || '';
  const statusFilter = STATUS_OPTIONS.includes(searchParams.get('status'))
    ? searchParams.get('status')
    : 'all';
  const hasFilters = !!urlSearch || statusFilter !== 'all';

  const writeParams = (updates, { replace = false } = {}) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === 'all' || value == null) params.delete(key);
      else params.set(key, String(value));
    });
    setSearchParams(params, { replace });
  };

  const setPagination = (nextPage, nextLimit = rowsPerPage, { replace = false } = {}) =>
    writeParams(
      {
        page: nextPage > 0 ? nextPage + 1 : '',
        limit: nextLimit !== DEFAULT_PAGE_SIZE ? nextLimit : '',
      },
      { replace }
    );

  const setFilter = (updates, { replace = false } = {}) =>
    writeParams({ ...updates, page: '' }, { replace });

  /**
   * `isCancelled` guards against out-of-order responses: a slower earlier
   * request must not overwrite a newer one, or the table renders rows for a
   * filter the user has already moved off. Supplied by the effect below;
   * user-initiated calls (Retry, refresh) pass nothing and always apply.
   */
  const load = useCallback(async ({ isCancelled = () => false } = {}) => {
    setLoading(true);
    try {
      const { projects, pagination } = await adminService.getProjects({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        ...(urlSearch ? { search: urlSearch } : {}),
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      });
      if (isCancelled()) return;
      setRows(projects || []);
      setTotal(pagination?.total ?? 0);
      setError(null);
    } catch (err) {
      if (isCancelled()) return;
      setError(err?.response?.data?.error || err?.message || 'Could not load projects.');
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, [page, rowsPerPage, urlSearch, statusFilter]);

  useEffect(() => {
    let cancelled = false;
    load({ isCancelled: () => cancelled });
    return () => { cancelled = true; };
  }, [load]);

  const [searchInput, setSearchInput] = useState(urlSearch);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.trim() !== urlSearch) {
        setFilter({ search: searchInput.trim() }, { replace: true });
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, urlSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSearchInput((current) => (current.trim() === urlSearch ? current : urlSearch));
  }, [urlSearch]);

  useEffect(() => {
    if (loading || total === 0 || page === 0) return;
    const lastPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
    if (page > lastPage) setPagination(lastPage, rowsPerPage, { replace: true });
  }, [loading, total, page, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ fontSize: 22, fontWeight: 700, color: text.primary, mb: 0.25 }}>
        Client Projects
      </Typography>
      <Typography variant="body2" sx={{ color: text.secondary, mb: 2.5 }}>
        {loading
          ? 'Loading…'
          : `${total.toLocaleString()} project${total === 1 ? '' : 's'}${hasFilters ? ' matching' : ''}`}
      </Typography>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        sx={{ mb: 2.5 }}
        alignItems={{ xs: 'stretch', md: 'center' }}
      >
        <TextField
          size="small"
          placeholder="Search by project title or client"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ flex: 1, minWidth: { md: 280 }, bgcolor: bg.paper }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: text.tertiary }} />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchInput('')} aria-label="Clear search">
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setFilter({ status: e.target.value })}
          sx={{ minWidth: 190, bgcolor: bg.paper }}
        >
          <MenuItem value="all">All statuses</MenuItem>
          {STATUS_OPTIONS.map((st) => (
            <MenuItem key={st} value={st} sx={{ textTransform: 'capitalize' }}>
              {titleise(st)}
            </MenuItem>
          ))}
        </TextField>

        {hasFilters && (
          <Button
            onClick={() => setFilter({ search: '', status: 'all' })}
            sx={{ textTransform: 'none', color: text.secondary, whiteSpace: 'nowrap' }}
          >
            Clear all
          </Button>
        )}
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2.5, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={panelSx}>
        <TableContainer>
          <Table size="medium" sx={tableSx}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((label) => (
                  <TableCell key={label}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {COLUMNS.map((label) => (
                      <TableCell key={label}>
                        <Skeleton height={28} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: text.tertiary }}>
                      {hasFilters ? 'No projects match these filters.' : 'No projects yet.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((project) => {
                  const tone = projectStatus[project.status] || statusTokens.neutral;
                  return (
                    <TableRow key={project.id} sx={tableRowSx}>
                      <TableCell>
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          onClick={() =>
                            navigate(`/admin/home/projects/${encodeURIComponent(project.id)}`, {
                              state: { listSearch: location.search, listPath: location.pathname },
                            })
                          }
                          sx={{
                            color: text.primary,
                            fontWeight: 500,
                            fontSize: 'inherit',
                            fontFamily: 'inherit',
                            textAlign: 'left',
                            cursor: 'pointer',
                            '&:hover': { color: brand.primary },
                          }}
                        >
                          {project.title || '(untitled)'}
                        </Link>
                        {project.location && (
                          <Typography variant="caption" sx={{ color: text.tertiary, display: 'block' }}>
                            {project.location}
                          </Typography>
                        )}
                        {/* One deal, two rows: link the client's listing and
                            the working project created when a quotation was
                            accepted, so they never read as separate projects. */}
                        {project.sourceProjectId && (
                          <Chip
                            size="small"
                            clickable
                            label="Working project — from accepted quotation"
                            onClick={() =>
                              navigate(`/admin/home/projects/${encodeURIComponent(project.sourceProjectId)}`, {
                                state: { listSearch: location.search, listPath: location.pathname },
                              })
                            }
                            sx={{
                              mt: 0.5, height: 20, fontSize: 10.5, fontWeight: 600,
                              color: statusTokens.successDark, bgcolor: statusTokens.successBg,
                            }}
                          />
                        )}
                        {project.workingProjectId && (
                          <Chip
                            size="small"
                            clickable
                            label="Listing — converted to a working project"
                            onClick={() =>
                              navigate(`/admin/home/projects/${encodeURIComponent(project.workingProjectId)}`, {
                                state: { listSearch: location.search, listPath: location.pathname },
                              })
                            }
                            sx={{
                              mt: 0.5, height: 20, fontSize: 10.5, fontWeight: 600,
                              color: text.secondary, bgcolor: bg.subtle,
                            }}
                          />
                        )}
                        {/* Accepted but never funded: the reminder job nudges
                            the client twice, then hands off to a person. */}
                        {project.awaitingDeposit && (
                          <Chip
                            size="small"
                            label="Awaiting deposit — contractor can't start"
                            sx={{
                              mt: 0.5, ml: project.sourceProjectId ? 0.5 : 0, height: 20, fontSize: 10.5, fontWeight: 600,
                              color: statusTokens.warningDark, bgcolor: statusTokens.warningBg,
                            }}
                          />
                        )}
                        {/* What the client told us happened to the listing. */}
                        {project.outcome && project.outcome !== 'still_deciding' && (
                          <Chip
                            size="small"
                            label={{
                              hired_elsewhere: `Hired outside Eqaya${project.outcomeReason ? ` — ${({ price: 'price', cash: 'wanted cash', contractor_direct: 'contractor went direct', too_slow: 'too slow', no_contractor: 'no contractor', other: 'other' })[project.outcomeReason] || project.outcomeReason}` : ''}`,
                              not_going_ahead: 'Not going ahead',
                              no_response: 'Paused — no response in 90 days',
                            }[project.outcome] || project.outcome}
                            sx={{
                              mt: 0.5, height: 20, fontSize: 10.5, fontWeight: 600,
                              color: project.outcome === 'hired_elsewhere' ? statusTokens.errorDark : statusTokens.neutral,
                              bgcolor: project.outcome === 'hired_elsewhere' ? statusTokens.errorBg : statusTokens.neutralBg,
                            }}
                          />
                        )}
                      </TableCell>

                      <TableCell sx={{ color: text.secondary }}>
                        {project.owner ? (
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() =>
                              navigate(`/admin/home/users/${project.owner.id}`, {
                                state: { listSearch: location.search, listPath: location.pathname },
                              })
                            }
                            sx={{
                              color: text.secondary,
                              fontSize: 'inherit',
                              fontFamily: 'inherit',
                              textAlign: 'left',
                              cursor: 'pointer',
                              '&:hover': { color: brand.primary },
                            }}
                          >
                            {project.owner.name || project.owner.email}
                          </Link>
                        ) : (
                          EMPTY
                        )}
                      </TableCell>

                      <TableCell sx={{ color: text.secondary }}>
                        {project.contractor?.name || project.contractor?.email || EMPTY}
                      </TableCell>

                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Chip
                          size="small"
                          label={titleise(project.status)}
                          sx={{
                            textTransform: 'capitalize',
                            fontWeight: 600,
                            fontSize: 11,
                            height: 22,
                            color: tone,
                            bgcolor: `${tone}1a`,
                          }}
                        />
                      </TableCell>

                      <TableCell sx={{ color: text.secondary, whiteSpace: 'nowrap' }}>
                        {project.budget ? formatCurrency(project.budget) : EMPTY}
                      </TableCell>

                      <TableCell sx={{ minWidth: 120 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, Math.max(0, project.completionPercentage))}
                            sx={{
                              flex: 1,
                              height: 6,
                              borderRadius: 3,
                              bgcolor: border.light,
                              '& .MuiLinearProgress-bar': { bgcolor: tone, borderRadius: 3 },
                            }}
                          />
                          <Typography variant="caption" sx={{ color: text.tertiary, minWidth: 30 }}>
                            {project.completionPercentage}%
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ color: text.secondary, whiteSpace: 'nowrap' }}>
                        {formatDate(project.createdAt)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, next) => setPagination(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setPagination(0, parseInt(e.target.value, 10))}
          rowsPerPageOptions={PAGE_SIZES}
          sx={{ borderTop: `1px solid ${border.light}` }}
        />
      </Paper>
    </Box>
  );
};

export default AdminClientProjectsPage;
